import type {
  ApiContext,
  ApiNotAuthorized,
  InvalidFormField,
  Scan,
  StoreCustomer,
  StoreNotFound,
} from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import { errorText } from "../primitives/errors";
import { shopEntryId } from "../primitives/workspace";
import type { CustomersRepository } from "../repositories/customers-repository";
import type { BackendApi } from "./api";

/** Fetches the customers into the repository. Single flight. */
export async function loadCustomers(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  locationId: string | null;
  customersRepository: CustomersRepository;
}): Promise<void> {
  if (o.customersRepository.status.kind === "loading") return;
  o.customersRepository.setLoading();
  const r = await o.authHandler.callApi((ctx) =>
    o.client.scans.customers(ctx, shopEntryId(o.shopId, o.locationId), { enableNoMatch: true }),
  );
  if (!r.ok) {
    o.customersRepository.setError(errorText(r.errors));
    return;
  }
  o.customersRepository.setCustomers(r.value);
}

/** One customer's scans at this counter. */
export function loadCustomerScans(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  locationId: string | null;
  customerId: string;
}): AsyncResult<Scan[], ApiNotAuthorized | StoreNotFound> {
  return o.authHandler.callApi((ctx) =>
    o.client.scans.list(ctx, shopEntryId(o.shopId, o.locationId), { customerId: o.customerId, enableNoMatch: true }),
  );
}

/** A counter (POS) customer: staff scan and charge for them. Takes the plain store id. */
export async function createPosCustomer(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  name: string;
  customersRepository: CustomersRepository;
}): AsyncResult<StoreCustomer, ApiNotAuthorized | StoreNotFound | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) => o.client.scans.createCustomer(ctx, o.shopId, o.name.trim()));
  if (r.ok) o.customersRepository.add(r.value);
  return r;
}
